import { useEffect, useState, useCallback } from "react";
import { api } from "./apiClient";

/**
 * useLandingConfig — Sesión 1 (cache)
 *
 * Carga la configuración pública del landing (testimonios, entidades, métricas)
 * con **caché persistente en localStorage** e **invalidación selectiva por sección**.
 *
 * Algoritmo:
 *  1. Lee localStorage → si hay cache válida, hidrata el state inmediatamente
 *     (UX instantánea, sin spinner si el usuario ya visitó la landing).
 *  2. Pide `GET /api/landing/versions` (~80 bytes, 1 sola request).
 *  3. Compara versions cacheadas vs nuevas:
 *     - Si la sección X tiene la misma version → usa cache.
 *     - Si la sección X cambió → hace `GET /api/landing/<X>` y actualiza solo esa.
 *  4. Persiste el nuevo estado en localStorage.
 *
 * Coste neto:
 *  - Primera visita: 1 (versions) + 3 (testimonials/entities/metrics) = 4 requests.
 *  - Visita siguiente sin cambios admin: **1 request** (versions, ~80 bytes).
 *  - Visita después de cambios solo en testimonios: **2 requests** (versions + testimonials).
 *
 * Devuelve: `{ testimonials, entities, metrics, loading, error, refetch }`
 *
 * `refetch()` fuerza recargar versions y secciones modificadas (útil tras logout, etc.).
 */

const STORAGE_KEY = "procure.landing.cache.v1";
const SECTIONS = ["testimonials", "entities", "metrics"];

// In-memory cache para responder síncronamente entre re-mounts del mismo tab.
let _memoryCache = null;

function _readCache() {
  if (_memoryCache) return _memoryCache;
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Validación defensiva del shape
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.versions || typeof parsed.versions !== "object") return null;
    const norm = {
      versions: {
        testimonials: Number(parsed.versions.testimonials) || 0,
        entities: Number(parsed.versions.entities) || 0,
        metrics: Number(parsed.versions.metrics) || 0,
      },
      testimonials: Array.isArray(parsed.testimonials) ? parsed.testimonials : [],
      entities: Array.isArray(parsed.entities) ? parsed.entities : [],
      metrics: Array.isArray(parsed.metrics) ? parsed.metrics : [],
      stored_at: parsed.stored_at || null,
    };
    _memoryCache = norm;
    return norm;
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[useLandingConfig] failed to parse localStorage cache:", err);
    }
    return null;
  }
}

function _writeCache(state) {
  const payload = {
    versions: state.versions,
    testimonials: state.testimonials,
    entities: state.entities,
    metrics: state.metrics,
    stored_at: new Date().toISOString(),
  };
  _memoryCache = payload;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    // QuotaExceeded u otros — silenciar, el cache es best-effort.
    if (process.env.NODE_ENV === "development") {
      console.warn("[useLandingConfig] failed to write localStorage cache:", err);
    }
  }
}

async function _fetchVersions() {
  const { data } = await api.get("/landing/versions");
  return {
    testimonials: Number(data?.testimonials) || 0,
    entities: Number(data?.entities) || 0,
    metrics: Number(data?.metrics) || 0,
  };
}

async function _fetchSection(section) {
  const { data } = await api.get(`/landing/${section}`);
  return {
    version: Number(data?.version) || 0,
    items: Array.isArray(data?.items) ? data.items : [],
  };
}

export function useLandingConfig() {
  const cached = _readCache();

  const [state, setState] = useState(() => ({
    testimonials: cached?.testimonials || [],
    entities: cached?.entities || [],
    metrics: cached?.metrics || [],
    versions: cached?.versions || { testimonials: 0, entities: 0, metrics: 0 },
    // Si tenemos cache, no mostramos loading (UX instantánea); solo loading si arrancamos vacíos.
    loading: !cached,
    error: null,
  }));

  const syncFromServer = useCallback(async () => {
    try {
      // 1. Pedir versions (siempre, liviano)
      const serverVersions = await _fetchVersions();
      const localVersions = (cached?.versions) || { testimonials: 0, entities: 0, metrics: 0 };

      // 2. Detectar qué secciones cambiaron
      const sectionsToRefetch = SECTIONS.filter(
        (s) => serverVersions[s] !== localVersions[s]
      );

      // 3. Si no hay cache previo, refetch las 3 (cold start).
      // Si hay cache, refetch solo las cambiadas.
      const targets = cached ? sectionsToRefetch : SECTIONS;

      if (targets.length === 0) {
        // Todo está al día — solo actualizamos versions (por si llegaron bumps de otras refs)
        const nextState = {
          testimonials: cached.testimonials,
          entities: cached.entities,
          metrics: cached.metrics,
          versions: serverVersions,
          loading: false,
          error: null,
        };
        setState(nextState);
        _writeCache(nextState);
        return;
      }

      // 4. Refetch las secciones cambiadas en paralelo
      const results = await Promise.all(targets.map((s) => _fetchSection(s)));

      const sectionData = {
        testimonials: cached?.testimonials || [],
        entities: cached?.entities || [],
        metrics: cached?.metrics || [],
      };
      targets.forEach((s, i) => {
        sectionData[s] = results[i].items;
      });

      const nextState = {
        ...sectionData,
        versions: serverVersions,
        loading: false,
        error: null,
      };
      setState(nextState);
      _writeCache(nextState);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[useLandingConfig] sync failed:", err);
      }
      // Si tenemos cache, dejarlo mostrado; si no, vaciar y reportar error.
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err,
      }));
    }
  }, [cached]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await syncFromServer();
    })();
    return () => { alive = false; };
    // Intencional: queremos correr solo en mount. El cached identity es estable
    // entre re-renders gracias al closure de _readCache().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    testimonials: state.testimonials,
    entities: state.entities,
    metrics: state.metrics,
    versions: state.versions,
    loading: state.loading,
    error: state.error,
    refetch: syncFromServer,
  };
}

/**
 * resetLandingConfigCache — limpia la caché local (memoria + localStorage).
 *
 * Útil:
 *  - En tests
 *  - Si admin acaba de cambiar config en la misma sesión y quiere forzar refresh
 *    (alternativa más liviana: solo llamar `refetch()` del hook, que ya detecta
 *     versions nuevas automáticamente).
 */
export function resetLandingConfigCache() {
  _memoryCache = null;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
